import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ModalForm } from 'components/ModalForm';
import { useModal } from 'context/ModalProvider/useModal';
import { ValidationGroup } from 'devextreme-react';
import {
  useLazyOfflineRequestWorkDetailQuery,
  useOfflineRequestWorkMoveToRequestMutation,
} from 'pages/OfflineRequests/api/offlineRequests';
import { OFFLINE_REQUEST_FORM_INITIAL } from 'pages/OfflineRequests/OfflineRequests.constants';
import { OfflineRequestsForm } from 'pages/OfflineRequests/OfflineRequests.entity';
import $createDataSource from 'utils/create-data-souce-instance';
import { isSuccess } from 'utils/responseStatus';

import { useLazyGetMethodOfReceptionListQuery } from 'bus/methodOfReception/services';

import { EditOfflineRequestsForm } from '../../components';

type Props = {
  id: number;
  onRefresh: () => void;
};

export const EditOfflineRequestsModal: FC<Props> = ({ id, onRefresh }) => {
  const { hideModal } = useModal();
  const modalValidateRef = useRef<ValidationGroup>(null);

  const [form, setForm] = useState<OfflineRequestsForm>(OFFLINE_REQUEST_FORM_INITIAL);

  const [offlineRequestWorkMoveToRequest] = useOfflineRequestWorkMoveToRequestMutation();
  const [offlineRequestWorkDetail, { data }] = useLazyOfflineRequestWorkDetailQuery();
  const [getMethodOfReceptionList, { data: methodOfReceptionList }] = useLazyGetMethodOfReceptionListQuery();

  const possibleRequests = useMemo(() => {
    return $createDataSource(`/v1/OfflineRequestWork/${id}/possibleRequests`, 'id');
  }, [id]);

  const storeTransport: any = useMemo(() => {
    const defaultFilter = ['transportType', '=', '1'];
    return $createDataSource('/v1/Transport', 'id', { defaultFilter });
  }, []);

  const storeEmployee: any = useMemo(() => {
    return $createDataSource('/v1/employee', 'id');
  }, []);

  const getInitData = useCallback(async () => {
    offlineRequestWorkDetail({ id }).then((data) => {
      if (isSuccess(data)) {
        data?.data && setForm(data.data);
      }
    });
  }, [offlineRequestWorkDetail, id]);

  useEffect(() => {
    getInitData();
  }, [getInitData]);

  const handleChange = (name: keyof OfflineRequestsForm, value: OfflineRequestsForm[keyof OfflineRequestsForm]) => {
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'requestId') {
      getMethodOfReceptionList({ requestId: value as number });
    }
  };

  const handleSubmit = () => {
    const isValid = modalValidateRef.current?.instance.validate().isValid;

    if (!isValid || !form) return;

    offlineRequestWorkMoveToRequest(form).then((data) => {
      if (isSuccess(data)) {
        onRefresh();
        hideModal();
        toast.success('Оффлайн заявка успешно обновлена', {
          position: 'bottom-right',
        });
      }
    });
  };

  return (
    <ModalForm
      onHidden={hideModal}
      title="Оффлайн заявка"
      visible
      isLoadingSubmit={false}
      width={500}
      height="auto"
      textConfirm="Сохранить"
      hideOnOutsideClick={false}
      onSubmit={handleSubmit}
      ref={modalValidateRef}
    >
      <EditOfflineRequestsForm
        possibleRequests={possibleRequests}
        storeTransport={storeTransport}
        form={form}
        detail={data}
        onChange={handleChange}
        storeEmployee={storeEmployee}
        methodOfReceptionList={methodOfReceptionList}
      />
    </ModalForm>
  );
};
